import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { BusinessAssociate } from '@pn/core/domain/intel/business-associate';
import { ApiError, getApiErrorMessage } from '@pn/core/errors';
import { useIntelAccess } from '@pn/core/permissions/isIntelAccessDenied';
import { useCompaniesStorage } from '@pn/core/storage';
import {
  type Province,
  westernCanadaProvinces,
} from '@pn/core/utils/provinces';
import { apiBAProvider } from '@pn/services/api/intel/apiBAProvider';
import { CustomSearchInput } from '@pn/ui/custom-components/CustomSearchInput';
import { CustomMergeArrow } from '@pn/ui/icons';
import { HighlightedTypography } from '@pn/ui/text/HighlightedTypography';
import { isEmpty, isNil, orderBy } from 'lodash-es';
import React from 'react';
import { InternalLink } from 'src/ui/components/InternalLink';
import { UpgradeAccountToAccessMessage } from 'src/ui/components/UpgradeAccountToAccessMessage';
import { companiesURL } from 'src/ui/pages';

type SearchResults = Record<string, BusinessAssociate[]>;

export function OwnerLookup() {
  const { companies } = useCompaniesStorage();
  const access = useIntelAccess();
  const isAccessDeniesToBas = access('bas').denied();
  const [searchResults, setSearchResults] = React.useState<SearchResults>({});
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [isFetching, setIsFetching] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [province, setProvince] = React.useState<Province>('AB');

  const handleProvince = (
    _event: React.MouseEvent<HTMLElement>,
    newProvince: Province
  ) => {
    setProvince((prevState) => newProvince ?? prevState);
    setSearchResults({});
  };

  const handleSearch = async () => {
    setErrorMessage(null);
    setIsFetching(true);
    setSearchResults({});
    let errorMsg = null;
    let results = {};
    try {
      results = await apiBAProvider.getBAsByProvinceAndSearchTerm(
        province,
        searchTerm
      );
    } catch (error) {
      if (error instanceof ApiError) {
        errorMsg = getApiErrorMessage(error);
      } else {
        errorMsg = 'An error occurred while fetching the data';
      }
    }
    setTimeout(() => {
      setSearchResults(results);
      setErrorMessage(errorMsg);
      setIsFetching(false);
    }, 1000);
  };

  const handleChangeSearchTerm = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    if (isEmpty(newSearchTerm)) setSearchResults({});
  };

  const resultsCount = Object.keys(searchResults).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        <ToggleButtonGroup
          value={province}
          size={'small'}
          exclusive
          onChange={handleProvince}
          color={'primary'}
          aria-label="report type"
          disabled={isFetching}
        >
          {westernCanadaProvinces.map((prov) => (
            <ToggleButton key={prov} value={prov}>
              {prov}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box
          sx={{
            flexGrow: 1,
            ml: 2,
          }}
        >
          <CustomSearchInput
            placeholder={'Name or code'}
            value={searchTerm}
            onChange={handleChangeSearchTerm}
            disabled={isFetching}
            fullWidth
            onEnter={handleSearch}
          />
        </Box>
      </Box>
      {isAccessDeniesToBas && <UpgradeAccountToAccessMessage />}
      <Box
        sx={{
          justifyContent: 'flex-end',
          display: 'flex',
          width: '100%',
        }}
      >
        <Button
          variant={'contained'}
          disabled={searchTerm.length < 4 || isAccessDeniesToBas}
          color={'secondary'}
          onClick={handleSearch}
          startIcon={<SearchIcon />}
        >
          Search BA
        </Button>
      </Box>
      <Divider />
      {isFetching && <LinearProgress />}
      {!isNil(errorMessage) && <Alert severity="warning">{errorMessage}</Alert>}
      <Box>
        {resultsCount > 0 && (
          <Typography
            variant={'subtitle1'}
            sx={{
              mb: 2,
            }}
          >
            Found {resultsCount} possible owner
            {resultsCount > 1 && <>s</>}
          </Typography>
        )}
        {isEmpty(searchResults) && !isFetching && isNil(errorMessage) && (
          <Typography variant={'subtitle2'}>
            Find the current owner of a BA by searching for the name or code.
            The results display a chain of ownership changes via historical
            amalgamation data.
          </Typography>
        )}
        {Object.keys(searchResults).map((key) => {
          const bas = searchResults[key];
          const companyName = key.split('(')[0].trim();
          const lastBa = bas.at(-1);

          const orderedBas = orderBy(
            bas,
            ['corporateStatus', 'statusEffectiveDate'],
            ['asc', 'desc']
          );

          const amalgamatedBasLength = orderedBas.filter(
            (ba) => ba.corporateStatus === 'AMALGAMATED'
          ).length;

          const matchedParentCompany = companies.find(
            ({ name }) => name === companyName
          );
          return (
            <Accordion key={key}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <BusinessIcon fontSize={'small'} />
                    {key}
                  </Box>

                  <Box
                    sx={{
                      mr: 1,
                    }}
                  >
                    <Chip
                      color={
                        lastBa?.corporateStatus === 'ACTIVE'
                          ? 'success'
                          : 'warning'
                      }
                      size={'small'}
                      label={lastBa?.corporateStatus}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Divider />
                <Box>
                  {amalgamatedBasLength === 0 && (
                    <Typography
                      sx={{ p: 2 }}
                      variant={'body2'}
                      color={'textSecondary'}
                      align={'center'}
                    >
                      Search criteria only matched the root BA.
                      <br />{' '}
                      {matchedParentCompany && (
                        <>
                          See the{' '}
                          <InternalLink
                            title={'View company'}
                            to={
                              companiesURL +
                              `/${matchedParentCompany?.id}/history-and-activity/ba-history`
                            }
                          >
                            {matchedParentCompany.name} BA history page
                          </InternalLink>{' '}
                          for all BAs they have amalgamated.
                        </>
                      )}
                    </Typography>
                  )}
                  {amalgamatedBasLength > 0 && (
                    <Box
                      sx={{
                        p: 2,
                        pb: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      {orderedBas.map((ba) => {
                        const wasAmalgamated =
                          ba.corporateStatus === 'AMALGAMATED';

                        const matchedCompany = companies.find(
                          ({ name }) => name === ba.name
                        );

                        return (
                          <Box component={'span'} key={ba.code}>
                            {wasAmalgamated && (
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <CustomMergeArrow
                                  sx={{
                                    opacity: 0.15,
                                    transform: 'scale(1.25, 1)',
                                    fontSize: 48,
                                  }}
                                />
                                <Typography
                                  variant={'caption'}
                                  color={'textSecondary'}
                                >
                                  {ba.statusEffectiveDate}
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <HighlightedTypography
                                variant={'body1'}
                                text={ba.name + ` (${ba.code})`}
                                search={searchTerm}
                                sx={{ pb: 1 }}
                              />
                              {!isNil(matchedCompany) && (
                                <InternalLink
                                  title={'View company'}
                                  to={
                                    companiesURL +
                                    `/${matchedCompany.id}/history-and-activity/ba-history`
                                  }
                                >
                                  <OpenInBrowserIcon />
                                </InternalLink>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
